/* SUMMARY
  * Imports
  * Components
  * CustomerNoteDialog: pop-up shown once when a role first meets a customer's note
*/

/* Imports */
import { Dialog, Button, Typography } from "@material-tailwind/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
/***/

/* Components */
import { isBlankNote } from "./CustomerNoteCallout";
/***/

/**
 * CustomerNoteDialog: the loud half of the note treatment.
 *
 * Fires once on entry -- when a planner selects a customer, or when an intervention is opened --
 * so the instruction cannot be scrolled past. Callers own the "once" part: this component only
 * renders what it is told to, and re-firing on every render is the failure mode that gets a feature
 * like this switched off by its users.
 *
 * A single acknowledge button, not a confirm/cancel pair: there is nothing here to decline.
 *
 * Renders nothing when the note is blank, so a caller that forgets to check cannot open an empty
 * modal shell over the form.
 *
 * @param open - whether the dialog is showing
 * @param note - the note text
 * @param customerName - whose note this is
 * @param onClose - dismiss callback
 */
export default function CustomerNoteDialog({
  open,
  note,
  customerName,
  onClose
}: {
  open: boolean,
  note?: string | null,
  customerName?: string,
  onClose: () => void
}) {
  if (isBlankNote(note)) return null;

  return (
    <Dialog
      open={open}
      handler={() => {}}
      size="xs"
      dismiss={{
        outsidePress: false,
        escapeKey: false
      }}
      className="outline-none focus:outline-none ring-0 shadow-xl"
    >
      <div tabIndex={-1} className="p-5 outline-none focus:outline-none">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-full bg-amber-100 border border-amber-200">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-700" />
          </div>

          <Typography variant="h5" color="blue-gray">
            {t("client:noteAlert:dialogTitle")}
          </Typography>
        </div>

        {customerName && (
          <Typography variant="small" className="mb-2 font-semibold text-gray-700">
            {customerName}
          </Typography>
        )}

        {/*
          * max-h + overflow-y so a note at the 1000-character ceiling scrolls inside the dialog
          * instead of pushing the acknowledge button off-screen.
          * whitespace-pre-line: these are written as checklists, one instruction per line.
          */}
        <Typography className="mb-6 max-h-[50vh] overflow-y-auto whitespace-pre-line break-words text-gray-700 text-sm font-normal">
          {note}
        </Typography>

        <div className="flex justify-end">
          <Button
            color="blue"
            onClick={onClose}
            className="outline-none focus:outline-none focus:ring-0 focus-visible:outline-none"
            style={{ outline: "none" }}
          >
            {t("client:noteAlert:acknowledge")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
/***/
